import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  IconButton,
  Slide,
  Snackbar,
} from '@mui/material';
import React, { useEffect } from 'react';
import { colors } from '../theme';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastMessage {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
}

interface ToastProps {
  toast: ToastMessage;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onClose }) => {
  const [open, setOpen] = React.useState(true);

  useEffect(() => {
    setOpen(true);
  }, [toast]);

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => onClose(toast.id), 300);
  };

  useEffect(() => {
    if (toast.duration !== 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, toast.duration || 5000);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast.duration]);

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircleIcon sx={{ fontSize: 24 }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 24 }} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 24 }} />;
      case 'info':
        return <InfoIcon sx={{ fontSize: 24 }} />;
      default:
        return null;
    }
  };

  const getSeverityColor = () => {
    switch (toast.type) {
      case 'success':
        return colors.primary.main;
      case 'error':
        return colors.status.error;
      case 'warning':
        return colors.status.warning;
      case 'info':
        return colors.status.info;
      default:
        return colors.status.info;
    }
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      TransitionComponent={Slide}
      sx={{ mb: 2 }}
    >
      <Alert
        severity={toast.type}
        icon={getIcon()}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleClose}
            sx={{
              '&:hover': {
                backgroundColor: colors.alpha.primary10,
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        sx={{
          width: '400px',
          maxWidth: '90vw',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          borderRadius: 2,
          border: `1px solid ${getSeverityColor()}20`,
          backdropFilter: 'blur(10px)',
          '& .MuiAlert-icon': {
            color: getSeverityColor(),
          },
          '& .MuiAlert-message': {
            width: '100%',
            py: 0.5,
          },
          animation: 'slideUp 0.3s ease-out',
          '@keyframes slideUp': {
            from: {
              transform: 'translateY(100%)',
              opacity: 0,
            },
            to: {
              transform: 'translateY(0)',
              opacity: 1,
            },
          },
        }}
      >
        {toast.title && (
          <AlertTitle sx={{ fontWeight: 700, mb: 0.5 }}>
            {toast.title}
          </AlertTitle>
        )}
        <Box sx={{ fontSize: '0.95rem' }}>{toast.message}</Box>
      </Alert>
    </Snackbar>
  );
};

export default Toast;
